import React, { useState, useEffect, useContext, useRef } from 'react';
import { trajectoryCalculator } from '../utils/trajectoryCalculator';
import { db, auth } from '../firebase';
import { doc, setDoc, getDoc, collection, query, orderBy, limit, getDocs, deleteDoc } from 'firebase/firestore';
import UserContext from '../context/UserContext';

const RocketTrajectorySimulator = ({ onClose }) => {
  const { user } = useContext(UserContext);
  const [gameState, setGameState] = useState('setup'); // setup, flying, complete, results
  const [trajectory, setTrajectory] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [score, setScore] = useState(0);
  const [missionScores, setMissionScores] = useState({
    'LEO Satellite': 0,
    'Geostationary Satellite': 0,
    'Polar Orbit': 0
  });
  const [totalScore, setTotalScore] = useState(0);
  const [globalHighScores, setGlobalHighScores] = useState([]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [mission, setMission] = useState({
    name: 'LEO Satellite',
    targetAltitude: 408000, // ISS altitude in meters
    targetInclination: 51.6, // ISS inclination
    launchSite: { latitude: 28.5619, longitude: -80.5773, name: 'Cape Canaveral' }
  });

  // Game parameters
  const [launchAzimuth, setLaunchAzimuth] = useState(180); // degrees - default to south
  const [boostDuration, setBoostDuration] = useState(0); // seconds - default to empty
  const [isDraggingAzimuth, setIsDraggingAzimuth] = useState(false);
  const [isDraggingFuel, setIsDraggingFuel] = useState(false);

  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const azimuthRef = useRef(null);
  const fuelRef = useRef(null);

  // Handle azimuth dragging
  const handleAzimuthDrag = (e) => {
    if (!azimuthRef.current) return;

    const rect = azimuthRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = e.clientX - centerX;
    const deltaY = centerY - e.clientY; // Invert Y axis

    const angle = Math.atan2(deltaX, deltaY) * (180 / Math.PI);
    const normalizedAngle = (angle + 360) % 360;

    // Round to nearest 5 degrees for easier precision
    const roundedAngle = Math.round(normalizedAngle / 5) * 5;
    setLaunchAzimuth(roundedAngle);
  };

  // Handle fuel tank dragging
  const handleFuelDrag = (e) => {
    if (!fuelRef.current) return;

    const rect = fuelRef.current.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const percentage = Math.max(0, Math.min(1, 1 - (relativeY / rect.height)));
    const newDuration = Math.round(percentage * 600);

    setBoostDuration(Math.max(60, Math.min(600, newDuration)));
  };

  // Handle direct fuel input
  const handleFuelInput = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 60 && value <= 600) {
      setBoostDuration(value);
    }
  };

  // Load high scores on component mount
  useEffect(() => {
    if (user) {
      loadMissionScores();
    }
  }, [user]);

  const loadMissionScores = async () => {
    if (!user) return;

    try {
      // Load user's mission scores
      const userScoreRef = doc(db, 'gameScores', 'rocketTrajectory', 'users', user.uid);
      const userScoreDoc = await getDoc(userScoreRef);
      if (userScoreDoc.exists()) {
        const data = userScoreDoc.data();
        const missionScoresData = data.missionScores || {
          'LEO Satellite': 0,
          'Geostationary Satellite': 0,
          'Polar Orbit': 0
        };
        setMissionScores(missionScoresData);

        // Calculate total score
        const total = Object.values(missionScoresData).reduce((sum, score) => sum + score, 0);
        setTotalScore(total);
      }

      // Load global high scores (total scores) with usernames
      const scoresQuery = query(
        collection(db, 'gameScores', 'rocketTrajectory', 'users'),
        orderBy('totalScore', 'desc'),
        limit(10)
      );
      const scoresSnapshot = await getDocs(scoresQuery);
      const scores = [];

      for (const docSnap of scoresSnapshot.docs) {
        const scoreData = docSnap.data();
        let displayName = 'Anonymous';

        // Try to get username from users collection
        try {
          const userDocRef = doc(db, 'users', docSnap.id);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists() && userDoc.data().username) {
            displayName = userDoc.data().username;
          }
        } catch (userError) {
          console.warn('Could not fetch username for user:', docSnap.id);
        }

        scores.push({
          uid: docSnap.id,
          displayName: displayName,
          ...scoreData
        });
      }

      setGlobalHighScores(scores);
    } catch (error) {
      console.error('Error loading mission scores:', error);
      // Don't show error to user, just silently fail for now
    }
  };

  const calculateScore = (trajectoryData, targetAlt, targetInc) => {
    if (!trajectoryData || !trajectoryData.positions || trajectoryData.positions.length === 0) {
      return 0;
    }

    const finalPosition = trajectoryData.positions[trajectoryData.positions.length - 1];
    const finalAltitude = finalPosition.altitude / 1000; // Convert to km
    const targetAltitudeKm = targetAlt / 1000;

    console.log('🎯 Calculating score:');
    console.log(`   Final altitude: ${finalAltitude.toFixed(1)} km`);
    console.log(`   Target altitude: ${targetAltitudeKm.toFixed(1)} km`);

    // Percentage-based accuracy scoring (fair for different altitude ranges)
    const altitudeErrorPercent = Math.abs(finalAltitude - targetAltitudeKm) / targetAltitudeKm;
    console.log(`   Altitude error: ${(altitudeErrorPercent * 100).toFixed(1)}%`);

    // Score based on percentage accuracy (0-1000 points)
    // Perfect match = 1000 points, 10% error = 900 points, 50% error = 500 points, etc.
    const altitudeScore = Math.max(0, 1000 * (1 - altitudeErrorPercent));
    console.log(`   Altitude score: ${altitudeScore.toFixed(1)}`);

    // Inclination would need more complex calculation, for now just altitude
    const totalScore = Math.round(altitudeScore);

    console.log(`   Final score: ${totalScore}`);
    return Math.max(0, totalScore);
  };

  const launchRocket = () => {
    try {
      console.log('🚀 Launching rocket with parameters:', {
        launchAzimuth,
        boostDuration,
        mission
      });

      // Create launch data for trajectory calculation
      const launchData = {
        launchPad: {
          latitude: mission.launchSite.latitude,
          longitude: mission.launchSite.longitude,
          name: mission.launchSite.name
        },
        rocket: {
          name: 'Game Rocket',
          stages: 1
        },
        mission: {
          name: mission.name
        },
        targetOrbit: {
          altitude: mission.targetAltitude,
          inclination: mission.targetInclination
        }
      };

      // Calculate trajectory with player parameters affecting the outcome
      const calculatedTrajectory = trajectoryCalculator.calculateGameTrajectory(launchData, launchAzimuth, boostDuration);

      console.log('Calculated trajectory:', calculatedTrajectory);

      if (calculatedTrajectory && calculatedTrajectory.positions && calculatedTrajectory.positions.length > 0) {
        // Set trajectory and immediately start animation with the data
        setTrajectory(calculatedTrajectory);
        setGameState('flying');
        setCurrentPosition(0);
        startTimeRef.current = Date.now();

        // Start animation with the calculated trajectory data directly
        setTimeout(() => animateTrajectory(calculatedTrajectory), 50);
      } else {
        console.error('Failed to calculate trajectory - invalid result:', calculatedTrajectory);
        alert('Failed to calculate trajectory. The trajectory calculator returned invalid data. Please try different parameters or contact support.');
      }
    } catch (error) {
      console.error('Error launching rocket:', error);
      alert('Error launching rocket. Please try again. Error: ' + error.message);
    }
  };

  const animateTrajectory = (trajectoryData) => {
    const animate = () => {
      if (!trajectoryData || !trajectoryData.positions || trajectoryData.positions.length === 0) {
        console.error('No trajectory data available for animation');
        completeFlight();
        return;
      }

      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min(elapsed / (trajectoryData.positions.length * 50), 1); // 50ms per position for smoother animation

      const newPosition = Math.floor(progress * trajectoryData.positions.length);
      setCurrentPosition(newPosition);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete
        completeFlight(trajectoryData);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  const completeFlight = (trajectoryData) => {
    // Calculate score using the trajectory data passed directly
    const finalScore = calculateScore(trajectoryData, mission.targetAltitude, mission.targetInclination);
    setScore(finalScore);

    // Update trajectory state for display purposes
    setTrajectory(trajectoryData);

    // Save mission score if user is logged in and it's a new high score for this mission
    if (user && finalScore > missionScores[mission.name]) {
      saveMissionScore(finalScore);
    }

    // Stay in 'flying' state but with animation complete - button will show
    // Don't change to 'complete' state to keep user on the trajectory view
  };

  const saveMissionScore = async (newScore) => {
    try {
      const userScoreRef = doc(db, 'gameScores', 'rocketTrajectory', 'users', user.uid);

      // Update mission scores
      const updatedMissionScores = {
        ...missionScores,
        [mission.name]: newScore
      };

      // Calculate new total score
      const newTotalScore = Object.values(updatedMissionScores).reduce((sum, score) => sum + score, 0);

      await setDoc(userScoreRef, {
        missionScores: updatedMissionScores,
        totalScore: newTotalScore,
        displayName: user.displayName || user.email?.split('@')[0] || 'Anonymous',
        timestamp: new Date(),
        userEmail: user.email
      });

      // Update local state
      setMissionScores(updatedMissionScores);
      setTotalScore(newTotalScore);

      // Reload global high scores
      loadMissionScores();
    } catch (error) {
      console.error('Error saving mission score:', error);
    }
  };

  const resetGame = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setGameState('setup');
    setTrajectory(null);
    setCurrentPosition(0);
    setScore(0);
  };

  const resetScores = async () => {
    if (!user) return;

    try {
      const userScoreRef = doc(db, 'gameScores', 'rocketTrajectory', 'users', user.uid);

      // Delete the user's score document entirely to remove them from leaderboard
      await deleteDoc(userScoreRef);

      // Update local state
      setMissionScores({
        'LEO Satellite': 0,
        'Geostationary Satellite': 0,
        'Polar Orbit': 0
      });
      setTotalScore(0);

      // Reload global high scores
      loadMissionScores();

      // Close modal
      setShowResetModal(false);

      // Show success message
      alert('Your scores have been reset! You can now start fresh.');
    } catch (error) {
      console.error('Error resetting scores:', error);
      alert('Failed to reset scores. Please try again.');
    }
  };

  const getMissionOptions = () => [
    {
      name: 'LEO Satellite',
      targetAltitude: 408000, // ISS altitude
      targetInclination: 51.6,
      launchSite: { latitude: 28.5619, longitude: -80.5773, name: 'Cape Canaveral' },
      description: 'Launch a satellite to Low Earth Orbit'
    },
    {
      name: 'Geostationary Satellite',
      targetAltitude: 35786000, // GEO altitude
      targetInclination: 0,
      launchSite: { latitude: 5.239, longitude: -52.768, name: 'Kourou' },
      description: 'Launch a communications satellite to GEO'
    },
    {
      name: 'Polar Orbit',
      targetAltitude: 800000,
      targetInclination: 90,
      launchSite: { latitude: 69.396, longitude: 20.971, name: 'Andøya' },
      description: 'Launch a polar orbiting satellite'
    }
  ];

  const selectMission = (selectedMission) => {
    console.log('🎯 Selecting mission:', selectedMission.name, 'inclination:', selectedMission.targetInclination);

    // Update mission state
    setMission(selectedMission);

    // Reset to default values for player to figure out (trial and error)
    setLaunchAzimuth(180); // Default to south
    setBoostDuration(0); // Default to empty

    console.log('🎮 Mission selected - player must figure out optimal parameters through trial and error');
  };

  return (
    <div className="fixed inset-0 bg-black z-40 pt-20">
      <div className="w-full h-full bg-gray-900 p-8 overflow-y-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-purple-400">🚀 Rocket Trajectory Simulator</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-3xl hover:bg-gray-800 rounded-lg px-3 py-1"
          >
            ×
          </button>
        </div>

        {gameState === 'setup' && (
          <div className="space-y-6">
            {/* Instructions - only show on first load */}
            {showInstructions && (
              <div className="bg-blue-900 bg-opacity-50 p-4 rounded-lg border border-blue-400">
                <h3 className="text-xl font-semibold text-blue-300 mb-3">🎮 How to Play</h3>
                <div className="text-gray-300 text-sm space-y-2">
                  <p><strong>1.</strong> Choose a mission type (LEO, GEO, or Polar orbit)</p>
                  <p><strong>2.</strong> Adjust launch parameters:</p>
                  <ul className="ml-4 space-y-1">
                    <li>• <strong>Azimuth:</strong> Direction to launch (0°=North, 90°=East, 180°=South, 270°=West)</li>
                    <li>• <strong>Boost Duration:</strong> How long the rocket engines fire (1-10 minutes)</li>
                  </ul>
                  <p><strong>3.</strong> Click "LAUNCH ROCKET" to start the mission</p>
                  <p><strong>4.</strong> Watch the trajectory and try to hit the target orbit altitude</p>
                  <p><strong>Goal:</strong> Get as close as possible to the target altitude for the highest score!</p>
                </div>
              </div>
            )}

            {/* How to Play and Reset Scores buttons */}
            <div className="text-center flex gap-4 justify-center">
              <button
                onClick={() => setShowInstructions(!showInstructions)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              >
                {showInstructions ? '❌ Hide Instructions' : '📖 How to Play'}
              </button>
              {user && (
                <button
                  onClick={() => setShowResetModal(true)}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  🔄 Reset Scores
                </button>
              )}
            </div>

            {/* Mission Selection */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Select Mission</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {getMissionOptions().map((option) => {
                  const isUnlocked = option.name === 'LEO Satellite' ||
                    (option.name === 'Geostationary Satellite' && missionScores['LEO Satellite'] >= 750) ||
                    (option.name === 'Polar Orbit' && missionScores['Geostationary Satellite'] >= 750);

                  return (
                    <button
                      key={option.name}
                      onClick={() => isUnlocked && selectMission(option)}
                      disabled={!isUnlocked}
                      className={`p-4 rounded-lg border-2 transition-colors relative ${
                        mission.name === option.name
                          ? 'border-purple-400 bg-purple-900'
                          : isUnlocked
                            ? 'border-gray-600 bg-gray-800 hover:border-purple-300'
                            : 'border-gray-700 bg-gray-900 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      {!isUnlocked && (
                        <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center rounded-lg">
                          <div className="text-center">
                            <div className="text-yellow-400 text-2xl mb-1">🔒</div>
                            <div className="text-white text-xs font-semibold">
                              {option.name === 'Geostationary Satellite'
                                ? `Score ${750 - missionScores['LEO Satellite']} more in LEO`
                                : `Score ${750 - missionScores['Geostationary Satellite']} more in GEO`}
                            </div>
                          </div>
                        </div>
                      )}
                      <h4 className="text-white font-semibold">{option.name}</h4>
                      <p className="text-gray-300 text-sm mt-2">{option.description}</p>
                      <p className="text-gray-400 text-xs mt-1">
                        Target: {(option.targetAltitude / 1000).toFixed(0)}km altitude
                      </p>
                      {isUnlocked && missionScores[option.name] > 0 && (
                        <p className="text-green-400 text-xs mt-1">
                          Best: {missionScores[option.name]} pts
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Launch Parameters */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">Launch Parameters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Azimuth Control - Circular Globe */}
                <div>
                  <label className="block text-gray-300 mb-2">
                    Launch Direction: {launchAzimuth.toFixed(0)}°
                  </label>
                  <div
                    ref={azimuthRef}
                    className="relative w-48 h-48 mx-auto bg-gradient-to-br from-blue-900 to-black rounded-full border-2 border-gray-600"
                  >
                    {/* Side-view globe showing equator left to right */}
                    <div className="absolute inset-2 rounded-full bg-gradient-to-r from-green-600 via-blue-400 to-green-600 opacity-90"></div>
                    <div className="absolute inset-3 rounded-full bg-gradient-to-r from-green-500 via-blue-300 to-green-500 opacity-70"></div>
                    <div className="absolute inset-4 rounded-full bg-gradient-to-r from-green-400 via-blue-200 to-green-400 opacity-50"></div>

                    {/* Continents as landmasses on the globe */}
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                      {/* Left continent */}
                      <ellipse cx="25" cy="50" rx="12" ry="35" fill="#2d5a27" opacity="0.8"/>
                      <ellipse cx="25" cy="50" rx="8" ry="25" fill="#3d7a37" opacity="0.6"/>

                      {/* Center continent/island */}
                      <ellipse cx="50" cy="45" rx="6" ry="20" fill="#2d5a27" opacity="0.8"/>
                      <ellipse cx="50" cy="45" rx="4" ry="15" fill="#3d7a37" opacity="0.6"/>

                      {/* Right continent */}
                      <ellipse cx="75" cy="50" rx="10" ry="30" fill="#2d5a27" opacity="0.8"/>
                      <ellipse cx="75" cy="50" rx="7" ry="20" fill="#3d7a37" opacity="0.6"/>
                    </svg>

                    {/* Compass directions - simple letters */}
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-center">
                      <div className="text-sm font-bold text-white">N</div>
                    </div>
                    <div className="absolute top-1/2 -right-6 transform -translate-y-1/2 text-center">
                      <div className="text-sm font-bold text-white">E</div>
                    </div>
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-center">
                      <div className="text-sm font-bold text-white">S</div>
                    </div>
                    <div className="absolute top-1/2 -left-6 transform -translate-y-1/2 text-center">
                      <div className="text-sm font-bold text-white">W</div>
                    </div>

                    {/* Launch site marker */}
                    <div className="absolute top-1/2 left-1/2 w-3 h-3 bg-red-500 rounded-full transform -translate-x-1/2 -translate-y-1/2">
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-white font-bold">
                        🚀
                      </div>
                    </div>

                    {/* Azimuth line */}
                    <div
                      className="absolute top-1/2 left-1/2 origin-bottom"
                      style={{
                        transform: `translate(-50%, -100%) rotate(${launchAzimuth}deg)`,
                        width: '2px',
                        height: '80px',
                        backgroundColor: '#fbbf24'
                      }}
                    >
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-b-4 border-transparent border-b-yellow-400"></div>
                    </div>

                    {/* Invisible drag area */}
                    <div
                      className="absolute inset-0 rounded-full cursor-pointer"
                      onMouseDown={(e) => {
                        setIsDraggingAzimuth(true);
                        handleAzimuthDrag(e);
                      }}
                      onMouseMove={(e) => isDraggingAzimuth && handleAzimuthDrag(e)}
                      onMouseUp={() => setIsDraggingAzimuth(false)}
                      onMouseLeave={() => setIsDraggingAzimuth(false)}
                    />
                  </div>
                  <div className="text-center text-xs text-gray-400 mt-4">
                    Drag the yellow arrow to set launch direction
                  </div>
                </div>

                {/* Fuel Tank Control */}
                <div>
                  <label className="block text-gray-300 mb-2">
                    Fuel Level: {(boostDuration / 6).toFixed(0)}%
                  </label>
                  <div
                    ref={fuelRef}
                    className="relative w-32 h-48 mx-auto bg-gray-800 rounded-lg border-2 border-gray-600 overflow-hidden"
                  >
                    {/* Fuel tank visualization */}
                    <div
                      className="absolute bottom-0 w-full bg-gradient-to-t from-orange-600 to-yellow-400 transition-all duration-300"
                      style={{ height: `${(boostDuration / 600) * 100}%` }}
                    />

                    {/* Tank markings */}
                    <div className="absolute inset-0 flex flex-col justify-between p-2">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="flex items-center">
                          <div className="w-2 h-0.5 bg-gray-400"></div>
                          <span className="text-xs text-gray-400 ml-1">{(100 - i * 20)}%</span>
                        </div>
                      ))}
                    </div>

                    {/* Fuel level indicator */}
                    <div
                      className="absolute right-0 top-0 bottom-0 w-1 bg-gray-600"
                      style={{ top: `${100 - (boostDuration / 600) * 100}%` }}
                    />

                    {/* Invisible drag area */}
                    <div
                      className="absolute inset-0 cursor-pointer"
                      onMouseDown={(e) => {
                        setIsDraggingFuel(true);
                        handleFuelDrag(e);
                      }}
                      onMouseMove={(e) => isDraggingFuel && handleFuelDrag(e)}
                      onMouseUp={() => setIsDraggingFuel(false)}
                      onMouseLeave={() => setIsDraggingFuel(false)}
                    />
                  </div>
                  <div className="text-center text-xs text-gray-400 mt-2">
                    Drag up/down to fill fuel tank, or use up/down arrows in the text box below for fine tuning
                  </div>
                  <div className="text-center text-sm text-gray-300 mt-1">
                    Boost Duration: {boostDuration}s
                  </div>
                  <div className="text-center mt-2">
                    <input
                      type="number"
                      value={boostDuration}
                      onChange={handleFuelInput}
                      min="60"
                      max="600"
                      step="1"
                      className="w-20 px-2 py-1 text-center bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-purple-400"
                      style={{ appearance: 'textfield' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Mission Info */}
            <div className="bg-gray-800 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">Current Mission: {mission.name}</h4>
              <p className="text-gray-300 text-sm mb-2">{mission.launchSite.name}</p>
              <p className="text-gray-300 text-sm">
                Target: {(mission.targetAltitude / 1000).toFixed(0)}km altitude, {mission.targetInclination}° inclination
              </p>
            </div>

            {/* High Scores */}
            {user && (
              <div className="bg-gray-800 p-4 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Your Total Score: {totalScore.toLocaleString()}</h4>
                <div className="text-gray-300 text-sm mb-3">
                  LEO: {missionScores['LEO Satellite']} | GEO: {missionScores['Geostationary Satellite']} | Polar: {missionScores['Polar Orbit']}
                </div>
                {globalHighScores.length > 0 && (
                  <div>
                    <h5 className="text-gray-300 text-sm mb-2">Global Leaderboard</h5>
                    <div className="space-y-1">
                      {globalHighScores.slice(0, 5).map((score, index) => (
                        <div key={score.uid} className="flex justify-between text-sm">
                          <span className="text-gray-300">#{index + 1} {score.displayName || score.userEmail?.split('@')[0] || 'Anonymous'}</span>
                          <span className="text-purple-400">{score.totalScore?.toLocaleString() || 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Launch Button */}
            <div className="text-center">
              <button
                onClick={launchRocket}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg text-xl font-bold transition-colors"
              >
                🚀 LAUNCH ROCKET
              </button>
            </div>
          </div>
        )}

        {gameState === 'flying' && trajectory && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-4">🚀 Rocket in Flight!</h3>
              <div className="bg-gray-800 p-6 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-purple-400">
                      {((currentPosition / trajectory.positions.length) * 100).toFixed(0)}%
                    </div>
                    <div className="text-gray-300 text-sm">Progress</div>
                  </div>
                  {trajectory.positions[currentPosition] && (
                    <>
                      <div>
                        <div className="text-2xl font-bold text-blue-400">
                          {(trajectory.positions[currentPosition].altitude / 1000).toFixed(0)}km
                        </div>
                        <div className="text-gray-300 text-sm">Altitude</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-400">
                          {trajectory.positions[currentPosition].velocity.toFixed(0)}m/s
                        </div>
                        <div className="text-gray-300 text-sm">Velocity</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-yellow-400">
                          {trajectory.positions[currentPosition].pitch.toFixed(1)}°
                        </div>
                        <div className="text-gray-300 text-sm">Pitch</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Simple trajectory visualization */}
            <div className="bg-gray-800 p-4 rounded-lg relative">
              <div className="relative bg-gradient-to-t from-blue-900 to-black rounded-lg overflow-hidden" style={{ height: '576px' }}>
                {/* Ground */}
                <div className="absolute bottom-0 w-full h-16 bg-green-600"></div>

                {/* Complete coordinate system restructure - start at top of green band */}
                <svg className="absolute inset-0 w-full h-full" width="100%" height="100%" viewBox="0 0 1000 576" preserveAspectRatio="none">
                  {/* Trajectory path - starts at top of ground band (y=512 in SVG coordinates, since green band is h-16 = 64px) */}
                  <path
                    d={`M 0,512 ${trajectory.positions.slice(0, currentPosition + 1).map((pos, index) => {
                      // Horizontal: progress through trajectory (0 to 1000)
                      const x = (index / trajectory.positions.length) * 1000;

                      // Vertical: altitude mapped from ground level (512) upward to top (0)
                      const maxAlt = mission.targetAltitude * 1.5;
                      const altitudeRatio = Math.min(pos.altitude / maxAlt, 1);
                      const y = 512 - (altitudeRatio * 512); // 512 (ground) to 0 (top)

                      return `L ${x},${y}`;
                    }).join(' ')}`}
                    stroke="#ff6b6b"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  {/* Rocket position - exactly on trajectory path */}
                  {trajectory.positions[currentPosition] && (() => {
                    const rocketLeft = (currentPosition / trajectory.positions.length) * 100;

                    const maxAlt = mission.targetAltitude * 1.5;
                    const altitudeRatio = Math.min(trajectory.positions[currentPosition].altitude / maxAlt, 1);
                    const rocketTop = 512 - (altitudeRatio * 512); // Same calculation as trajectory

                    return (
                      <circle
                        cx={`${rocketLeft}%`}
                        cy={rocketTop}
                        r="6"
                        fill="#ef4444"
                        stroke="#ffffff"
                        strokeWidth="2"
                      />
                    );
                  })()}
                </svg>

                {/* Rocket emoji - positioned exactly over the SVG circle */}
                {trajectory.positions[currentPosition] && (() => {
                  const rocketLeft = (currentPosition / trajectory.positions.length) * 100;

                  const maxAlt = mission.targetAltitude * 1.5;
                  const altitudeRatio = Math.min(trajectory.positions[currentPosition].altitude / maxAlt, 1);
                  const rocketTop = 512 - (altitudeRatio * 512) - 12; // Above the circle, same calculation as trajectory

                  return (
                    <div
                      className="absolute text-sm text-white font-bold z-20 pointer-events-none"
                      style={{
                        left: `${rocketLeft}%`,
                        top: `${rocketTop}px`, // Same coordinate system as SVG
                        transform: 'translateX(-50%)'
                      }}
                    >
                      🚀
                    </div>
                  );
                })()}

                {/* Altitude scale - positioned relative to full 576px height */}
                <div className="absolute right-2 top-2 bottom-2 flex flex-col justify-between text-xs text-gray-300 font-mono z-20">
                  <div className="text-right">
                    <div className="text-white font-bold">{(mission.targetAltitude / 1000 * 1.5).toFixed(0)}km</div>
                    <div className="text-gray-400">Max Altitude</div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-bold">0km</div>
                    <div className="text-green-400">Earth Surface</div>
                  </div>
                </div>

                {/* Scale lines - positioned relative to full 576px height */}
                <div className="absolute right-12 top-2 w-px h-full bg-gray-600 opacity-50 z-10"></div>
                <div className="absolute right-12 bottom-16 w-8 border-t border-dashed border-green-400 opacity-70 z-10"></div>

                {/* Target orbit line - positioned relative to available plotting area (512px) */}
                <div
                  className="absolute w-full border-t-2 border-dashed border-purple-400 z-10"
                  style={{
                    top: `${512 - ((mission.targetAltitude / (mission.targetAltitude * 1.5)) * 512)}px`
                  }}
                >
                  <div className="absolute -top-7 right-16 text-sm text-purple-400 font-bold">
                    Target Orbit ({(mission.targetAltitude / 1000).toFixed(0)}km)
                  </div>
                </div>
              </div>

              {/* SEE RESULTS button - below the flight header when complete */}
              {((currentPosition / trajectory.positions.length) * 100).toFixed(0) === '100' && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => setGameState('results')}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg text-xl font-bold transition-colors shadow-lg"
                  >
                    📊 SEE RESULTS
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {gameState === 'complete' && trajectory && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-4">🎯 Mission Complete!</h3>
              <p className="text-gray-300 mb-6">Your rocket has reached orbit. Click below to see your results.</p>

              <div className="bg-gray-800 p-6 rounded-lg mb-6">
                <div className="text-4xl mb-4">🚀</div>
                <div className="text-lg text-gray-300 mb-4">
                  Final Altitude: {(trajectory.positions[trajectory.positions.length - 1].altitude / 1000).toFixed(1)} km
                </div>
                <div className="text-sm text-gray-400">
                  Target: {(mission.targetAltitude / 1000).toFixed(1)} km
                </div>
              </div>

              <button
                onClick={() => setGameState('results')}
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg text-xl font-bold transition-colors"
              >
                📊 SEE RESULTS
              </button>
            </div>
          </div>
        )}

        {gameState === 'results' && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Mission Complete! 🎉</h3>

              <div className="bg-gray-800 p-6 rounded-lg mb-6">
                <div className="text-6xl mb-4">
                  {score >= 800 ? '🏆' : score >= 600 ? '🥈' : score >= 400 ? '🥉' : '🎯'}
                </div>
                <div className="text-4xl font-bold text-purple-400 mb-2">
                  {score.toLocaleString()} Points
                </div>
                <div className="text-gray-300">
                  {score >= 800 ? 'Perfect launch!' :
                   score >= 600 ? 'Excellent trajectory!' :
                   score >= 400 ? 'Good job!' : 'Keep practicing!'}
                </div>
              </div>

              {trajectory && trajectory.positions.length > 0 && (
                <div className="bg-gray-800 p-4 rounded-lg text-left">
                  <h4 className="text-white font-semibold mb-3">Flight Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Final Altitude:</span>
                      <div className="text-white font-semibold">
                        {(trajectory.positions[trajectory.positions.length - 1].altitude / 1000).toFixed(1)} km
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400">Target Altitude:</span>
                      <div className="text-white font-semibold">
                        {(mission.targetAltitude / 1000).toFixed(1)} km
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400">Max Velocity:</span>
                      <div className="text-white font-semibold">
                        {Math.max(...trajectory.positions.map(p => p.velocity)).toFixed(0)} m/s
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-400">Flight Time:</span>
                      <div className="text-white font-semibold">
                        {trajectory.positions.length * 3}s
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4 justify-center mt-6">
                <button
                  onClick={resetGame}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  🎮 Play Again
                </button>
                <button
                  onClick={() => setGameState('setup')}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  ⚙️ Change Mission
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Scores Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-600 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-white mb-4">⚠️ Reset All Scores</h3>
              <p className="text-gray-300 mb-4">
                Are you sure you want to reset all your mission scores back to zero? This action cannot be undone.
              </p>
              <p className="text-red-400 text-sm mb-6">
                This will reset your progress on all missions (LEO, GEO, and Polar) and remove you from the leaderboard until you play again.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowResetModal(false)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={resetScores}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Yes, Reset Scores
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RocketTrajectorySimulator;
